import * as APIRoutes from '@mm/api-routes';
import { SessionObject } from '../interfaces/session';
import CompanyModel from '../model/CompanyModel';
import { UserAttributes, UTM } from '../model/CompanyUsersModel';
import AuthenticatedController from './AuthenticatedController';
import Controller from './Controller';

export const enum LoginType {
  // START used as route param
  funnelpro = 'marketers',
  smb = 'smb',
  agency = 'agencies',
  invite = 'invite',
  mobile = 'mobile',
  whiteLabel = 'whiteLabel',
  // END used as route param
  ambassador = 'ambassador',
}

export type Persona = CompanyModel['persona'] | undefined;

export const mapLoginTypeToPersona = (type?: LoginType): Persona => {
  if (!type || type === LoginType.invite) {
    return undefined;
  }

  switch (type) {
    case LoginType.agency:
      return 'agency';
    case LoginType.funnelpro:
      return 'marketer';
    case LoginType.smb:
      return 'smb';
    default:
      return undefined;
  }
};

export default class SessionController {
  static signup(args: {
    email: string;
    password: string;
    companyName?: string;
    website?: string;
    phone?: string;
    crm?: string;
    additionalFeatures?: string[];
    firstName?: string;
    lastName?: string;
    inviteToken?: string;
    freeTrial?: boolean;
    userAttributes?: Partial<UserAttributes> & UTM;
  }): Promise<SessionObject> {
    const validated = Object.fromEntries(
      Object.entries(args).filter(([_, value]) => !!value),
    );

    const params = {
      ...validated,

      // API branches user setup flows based on this parameter
      // with true value API will create default bot for the user company
      emailOnlySignup: true,
    };

    return Controller.post(APIRoutes.SIGNUP, params);
  }

  static login(args: {
    email: string;
    password: string;
    inviteToken?: string;
  }): Promise<SessionObject> {
    return Controller.post(APIRoutes.LOGIN, args);
  }

  static requestResetPassword(args: {
    email: string;
  }): Promise<{ success: boolean }> {
    return Controller.post(APIRoutes.RESET_PASSWORD, args);
  }

  static confirmResetPassword(args: {
    token: string;
    password: string;
  }): Promise<{ success: boolean }> {
    return Controller.post(APIRoutes.RESET_PASSWORD_CONFIRM, {
      user: args,
    });
  }

  static validateResetPasswordToken(args: {
    token: string;
  }): Promise<{ email?: string }> {
    return Controller.get(APIRoutes.RESET_PASSWORD, args);
  }

  static validateEmailVerifyCode({
    verificationCode,
  }: {
    verificationCode: string;
  }): Promise<{
    success: boolean;
    errors?: { code?: number[] };
    user?: Partial<SessionObject>;
  }> {
    return AuthenticatedController.get(
      APIRoutes.VERIFY_EMAIL(verificationCode),
    );
  }

  static resendEmailVerifyCode(): Promise<{ success: boolean }> {
    return AuthenticatedController.post(APIRoutes.VERIFY_EMAIL_REQUEST);
  }

  static loginFacebook(body: {
    facebookToken: string;
    inviteToken?: string;
    standaloneToken?: string;
  }) {
    return Controller.post(APIRoutes.SESSION_FACEBOOK, body);
  }
}
